import type { CustomFile } from '../../types';

// ----------------------------------------------------------------------------

/*
 * Returned by a custom validator to leave a file out of the result entirely —
 * neither accepted nor rejected. A symbol and not a string because every other
 * value a validator returns is an error message: the string this replaces was
 * 'ignore', so a validator rejecting a file with that word as its reason had it
 * silently let through instead.
 */
export const SKIP_FILE: unique symbol = Symbol('validateFiles.skip');

export type TypeFileValidationCustomResult = string | string[] | typeof SKIP_FILE | null | undefined;

export type TypeValidateFiles = {
    /*
     * Extensions and MIME types together, either of which is enough to let a file
     * through. MIME wildcards are honoured, as they are on the HTML attribute this
     * borrows its name from.
     */
    accept?: string[];
    acceptUnknown?: boolean;
    extensions?: string[];
    size?: number;
    // MIME types, wildcards included.
    types?: string[];
    concurrency?: number;
    /*
     * How long the pass may hold the main thread before handing it back, in
     * milliseconds. Yielding by file count instead — one every N — paid the
     * timers' 4ms clamp per yield whether or not there was anything to wait for,
     * so ten thousand files spent the best part of a second yielding around some
     * twenty milliseconds of work.
     */
    yieldEveryMs?: number;
    /*
     * Stops the pass. What comes back is only what had been decided by then — a
     * file left unvisited is neither accepted nor rejected nor skipped — so the
     * caller checks the signal rather than reading anything into the result.
     */
    signal?: AbortSignal;
    fnCustomValidator?: (file: CustomFile) => TypeFileValidationCustomResult | Promise<TypeFileValidationCustomResult>;
};

export type TypeFileValidateResult = {
    accepted: CustomFile[];
    rejected: {
        file: CustomFile;
        errors: string[];
    }[];
    skipped: CustomFile[];
};

/*
 * What was decided about one file. Null — the initial state — means the pass
 * never reached it, which only an abort can leave behind.
 */
export type TypeFileOutcome = 'accepted' | 'skipped' | { errors: string[] };
